import { inject, Injectable } from '@angular/core';
import { AuthRepository } from './auth.repository';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { HttpResponse } from '@isaia/entity/http';
import { AuthUser } from './auth-user.model';
import { DialogService } from '@isaia/components/dialog';
import { ApplicationRepository } from '../application';
import { msalInstance, LOGIN_SCOPES } from './msal';
import { withAuthentication, withoutAutoLogout } from './auth.context';
import { withErrorPopup } from '../http/http-error.context';
import { AuthGuard } from './auth.guard';
import { createAuthUserFromMe, MeDto, REQUIRED_USER_PROPS } from './auth-profile.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly meUrl = environment.api.me;
  private readonly httpClient = inject(HttpClient);
  private readonly authRepository = inject(AuthRepository);
  private readonly dialogService = inject(DialogService);
  private readonly applicationRepository = inject(ApplicationRepository);
  private readonly authGuard = inject(AuthGuard);

  /**
   * Runs at app startup (before routing): initialise MSAL, complete any pending
   * sign-in redirect, and load the user profile when a session exists. This
   * makes $isLoggedIn accurate before the route guards run.
   */
  public async initAuth(): Promise<void> {
    try {
      // Dev/test password session (v2): reuse the stored token, skip MSAL.
      if (this.authRepository.$devToken()) {
        try {
          await this.loadProfile({ skipAutoLogout: true });
          return;
        } catch {
          // Stale dev token: clear it and fall through to MSAL, so the
          // Microsoft login option still works from the login screen.
          this.authRepository.setDevToken(undefined);
          this.authRepository.setUser(undefined);
        }
      }

      await msalInstance.initialize();

      const redirectResult = await msalInstance.handleRedirectPromise();
      if (redirectResult?.account) {
        msalInstance.setActiveAccount(redirectResult.account);
      } else if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length) {
        msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
      }

      if (msalInstance.getActiveAccount()) {
        try {
          await this.loadProfile({ skipAutoLogout: true });
        } catch {
          // Signed in with Microsoft, but the API rejected the token (e.g. /me
          // returns 401 when the backend's Entra config is wrong). Do NOT fire an
          // MSAL logout redirect here — it shows Microsoft's "pick an account to
          // sign out" screen and loops. Clear the session locally and surface an
          // error on the login screen instead.
          this.authRepository.setUser(undefined);
          this.authRepository.setAuthError(
            'Sign-in could not be completed. Please try again, or contact support if the problem persists.',
          );
          await msalInstance.clearCache().catch(() => undefined);
          msalInstance.setActiveAccount(null);
        }
      }
    } finally {
      // Unblock the route guards regardless of outcome (see AuthRepository.ready).
      this.authRepository.markReady();
    }
  }

  public login(): Promise<void> {
    return msalInstance.loginRedirect({ scopes: LOGIN_SCOPES });
  }

  /** Dev/test password sign-in (v2 only). Stores a local token and loads the profile. */
  public async devLogin(email: string, password: string): Promise<void> {
    const res = await firstValueFrom(
      this.httpClient.post<HttpResponse<{ token: string }>>(
        environment.api.devLogin,
        { email, password },
        // No auth header (there is no session yet) and no generic error popup:
        // the login form shows its own inline "login failed" message.
        { context: withAuthentication({ authentication: false, context: withErrorPopup(false) }) },
      ),
    );
    this.authRepository.setDevToken(res.data.token);
    await this.loadProfile();
  }

  public async loadProfile(options?: { skipAutoLogout?: boolean }): Promise<void> {
    const config = options?.skipAutoLogout ? { context: withoutAutoLogout() } : {};
    const res = await firstValueFrom(this.httpClient.get<HttpResponse<MeDto>>(this.meUrl, config));
    const user = createAuthUserFromMe(res.data);
    this.authRepository.setUser(user);
    this.throwErrorOnInvalidUser(user);
  }

  public async logout(): Promise<void> {
    this.applicationRepository.setLoading(true);
    const wasDevSession = !!this.authRepository.$devToken();
    this.authRepository.setUser(undefined);
    this.authRepository.setDevToken(undefined);
    this.authRepository.setStore(undefined);

    // Dev/test session has no MSAL account; just return to the login screen.
    if (wasDevSession) {
      this.applicationRepository.setLoading(false);
      await this.authGuard.redirectToNotLoggedRoute();
      return;
    }

    // No MSAL account (e.g. logout triggered before any sign-in completed):
    // a logoutRedirect here would show Microsoft's account picker in sign-out
    // mode for unrelated browser sessions. Just return to the login screen.
    const account = msalInstance.getActiveAccount();
    if (!account) {
      this.applicationRepository.setLoading(false);
      await this.authGuard.redirectToNotLoggedRoute();
      return;
    }
    await msalInstance.logoutRedirect({ account });
  }

  private throwErrorOnInvalidUser(user?: AuthUser) {
    const invalidKeys = REQUIRED_USER_PROPS.filter((prop) => !user?.[prop]);
    if (invalidKeys.length) {
      const message = `Invalid user, missed keys: ${invalidKeys.join(', ')}.\nPlease contact the assistance.`;
      this.dialogService.openPopup({ title: 'Auth Error', message }, { disableClose: true });
      throw new Error(message);
    }
  }
}
